import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const serviceOptions = [
  { value: 'manufacturing', label: 'Couch Manufacturing' },
  { value: 'sales', label: 'Buy a Couch' },
  { value: 'reupholstery', label: 'Couch Reupholstery' },
  { value: 'custom', label: 'Custom Design Request' },
];

export const ContactForm = ({ variant = 'default' }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    service: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleServiceChange = (value) => {
    setFormData(prev => ({ ...prev, service: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.name || !formData.email || !formData.phone || !formData.service || !formData.message) {
      toast.error('Please fill in all fields');
      return;
    }

    setIsSubmitting(true);
    try {
      await axios.post(`${API}/contact`, formData);
      toast.success('Message sent successfully! We\'ll get back to you soon.');
      setFormData({ name: '', email: '', phone: '', service: '', message: '' });
    } catch (error) {
      toast.error('Failed to send message. Please try again.');
      console.error('Contact form error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const isCompact = variant === 'compact';

  return (
    <motion.form
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      onSubmit={handleSubmit}
      data-testid="contact-form"
      className={`space-y-6 ${isCompact ? '' : 'bg-white rounded-2xl shadow-card p-8'}`}
    >
      <div className={`grid gap-6 ${isCompact ? 'grid-cols-1' : 'grid-cols-1 md:grid-cols-2'}`}>
        {/* Name */}
        <div className="space-y-2">
          <Label htmlFor="name" className="text-leather font-body font-medium">
            Full Name *
          </Label>
          <Input
            id="name"
            name="name"
            value={formData.name}
            onChange={handleChange}
            placeholder="John Doe"
            data-testid="contact-name-input"
            className="rounded-lg border-border focus:border-gold focus:ring-gold"
          />
        </div>

        {/* Email */}
        <div className="space-y-2">
          <Label htmlFor="email" className="text-leather font-body font-medium">
            Email Address *
          </Label>
          <Input
            id="email"
            name="email"
            type="email"
            value={formData.email}
            onChange={handleChange}
            placeholder="john@example.com"
            data-testid="contact-email-input"
            className="rounded-lg border-border focus:border-gold focus:ring-gold"
          />
        </div>

        {/* Phone */}
        <div className="space-y-2">
          <Label htmlFor="phone" className="text-leather font-body font-medium">
            Phone Number *
          </Label>
          <Input
            id="phone"
            name="phone"
            type="tel"
            value={formData.phone}
            onChange={handleChange}
            placeholder="+27 XX XXX XXXX"
            data-testid="contact-phone-input"
            className="rounded-lg border-border focus:border-gold focus:ring-gold"
          />
        </div>

        {/* Service Dropdown */}
        <div className="space-y-2">
          <Label htmlFor="service" className="text-leather font-body font-medium">
            Service Required *
          </Label>
          <Select value={formData.service} onValueChange={handleServiceChange}>
            <SelectTrigger 
              data-testid="contact-service-select"
              className="rounded-lg border-border focus:border-gold focus:ring-gold"
            >
              <SelectValue placeholder="Select a service" />
            </SelectTrigger>
            <SelectContent>
              {serviceOptions.map((option) => (
                <SelectItem 
                  key={option.value} 
                  value={option.value}
                  data-testid={`service-option-${option.value}`}
                >
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Message */}
      <div className="space-y-2">
        <Label htmlFor="message" className="text-leather font-body font-medium">
          Your Message *
        </Label>
        <Textarea
          id="message"
          name="message"
          value={formData.message}
          onChange={handleChange}
          placeholder="Tell us about your requirements..."
          rows={5}
          data-testid="contact-message-input"
          className="rounded-lg border-border focus:border-gold focus:ring-gold resize-none"
        />
      </div>

      {/* Submit Button */}
      <Button
        type="submit"
        disabled={isSubmitting}
        data-testid="contact-submit-btn"
        className="w-full md:w-auto btn-gold rounded-full px-10 py-3 text-base font-semibold"
      >
        {isSubmitting ? (
          <>
            <i className="fas fa-spinner fa-spin mr-2"></i>
            Sending...
          </>
        ) : (
          <>
            Send Message
            <i className="fas fa-paper-plane ml-2"></i>
          </>
        )}
      </Button>
    </motion.form>
  );
};

export default ContactForm;
